import React from 'react';

// A notice's message as page content that runs nothing (S-26 pt 4). Notices carry names and titles as people typed
// them (a learner's name in "<name> provided feedback for <course>"), so the message is never inserted as HTML: its text
// shows as text, bold stays bold, and a link stays a link with its class and its data-* values (the click handlers read
// them) and no address. The notices the server writes use none, and an address a person typed could lead off the site.
// Any other markup shows as its text only, and no attribute that runs anything survives. The message is read with
// DOMParser, whose document loads nothing and runs nothing.
// Removed 17 Oct 2026 (S-26 pt 4): keeping a link's web or same-site address, which a typed name could use to lead off
// the site (an outside address, or '/\host', which browsers read as another site).
const KEPT_TAGS = { B: 'b', STRONG: 'strong', I: 'i', EM: 'em' };

function toElements(nodes, linkStyle, keyPrefix) {
    const out = [];
    nodes.forEach((node, i) => {
        const key = `${keyPrefix}${i}`;
        if (node.nodeType === 3) {
            out.push(node.textContent);
            return;
        }
        if (node.nodeType !== 1) return;
        const tag = node.tagName;
        if (tag === 'BR') {
            out.push(React.createElement('br', { key }));
            return;
        }
        const children = toElements(Array.from(node.childNodes), linkStyle, `${key}-`);
        if (tag === 'A') {
            // With no address, the hand pointer comes from the style, and the keyboard reaches the link and opens it
            // with Enter, as it did when the link had its address.
            const props = {
                key,
                style: { cursor: 'pointer', ...linkStyle },
                role: 'link',
                tabIndex: 0,
                onKeyDown: (e) => {
                    if (e.key === 'Enter') e.currentTarget.click();
                },
            };
            if (node.getAttribute('class')) props.className = node.getAttribute('class');
            Array.from(node.attributes).forEach((attr) => {
                if (/^data-[a-z0-9-]+$/i.test(attr.name)) props[attr.name] = attr.value;
            });
            out.push(React.createElement('a', props, ...children));
        } else if (KEPT_TAGS[tag]) {
            out.push(React.createElement(KEPT_TAGS[tag], { key }, ...children));
        } else {
            out.push(...children);
        }
    });
    return out;
}

export function renderNotificationMessage(message, linkStyle) {
    if (!message) return null;
    const doc = new DOMParser().parseFromString(`<body>${message}</body>`, 'text/html');
    return toElements(Array.from(doc.body.childNodes), linkStyle, 'n');
}

export default renderNotificationMessage;
